import React, { createContext, useState, useContext } from 'react'

interface DrawerContextType {
  open: boolean
  openDrawer: (content: React.ReactNode, onClose?: () => void) => void
  openNestedDrawer: (content: React.ReactNode) => void
  closeDrawer: () => void
  content: React.ReactNode | null
  nestedContent: React.ReactNode | null
  closeNestedDrawer: () => void
  isNestedOpen: boolean
}

const DrawerContext = createContext<DrawerContextType | undefined>(undefined)

export const DrawerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [content, setContent] = useState<React.ReactNode | null>(null)
  const [nestedContent, setNestedContent] = useState<React.ReactNode | null>(null)
  const [isNestedOpen, setIsNestedOpen] = useState(false)
  const [onCloseCallback, setOnCloseCallback] = useState<(() => void) | null>(null)

  const openDrawer = (newContent: React.ReactNode, onClose?: () => void) => {
    setContent(newContent)
    setOnCloseCallback(() => onClose || null)
    setOpen(true)
  }
  const closeDrawer = () => {
    setOpen(false)
    setContent(null)
    if (onCloseCallback) {
      onCloseCallback() 
      setOnCloseCallback(null) 
    }
  }

  const openNestedDrawer = (newContent: React.ReactNode) => {
    setNestedContent(newContent)
    setIsNestedOpen(true)
  }

  const closeNestedDrawer = () => {
    setIsNestedOpen(false)
    setNestedContent(null)
  }

  return (
    <DrawerContext.Provider
      value={{
        open,
        openDrawer,
        closeDrawer,
        content,
        openNestedDrawer,
        nestedContent,
        closeNestedDrawer,
        isNestedOpen,
      }}
    >
      {children}
    </DrawerContext.Provider>
  )
}

export const useDrawer = () => {
  const context = useContext(DrawerContext)
  if (context === undefined) {
    throw new Error('useDrawer must be used within a DrawerProvider')
  }
  return context
}
